<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useSeo from '@/composables/useSeo';
import { getStandardSeoSuffixes } from '@/utils/seoConstants';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';
import { useOfficialProfilesStore } from '~/stores/apostatudo/official-profiles';
import { mapOfficialProfileToAffiliateConfig } from '~/utils/apostatudo/official-profile-mapper';

const route = useRoute();
const router = useRouter();
const perfil = computed(() => route.params.perfil as string);
const promocodeFromQuery = computed(
  () => route.query.promocode as string | undefined,
);

const shouldRenderAffiliateContent = computed(() => {
  return route.path.startsWith('/perfil-oficial');
});

const officialProfilesStore = useOfficialProfilesStore();

const isLoading = ref(true);
const affiliateConfigData = ref<AffiliateConfig | undefined>(undefined);
const perfilSlug = ref('');
const bannerCategorySlug = ref<string | undefined>(undefined);

const loadAffiliateConfig = async () => {
  isLoading.value = true;

  const { standardTitleSuffix } = getStandardSeoSuffixes();

  perfilSlug.value = perfil.value?.toLowerCase();

  // O slug da rota é o slug canônico do core — sem normalização.
  const profile = await officialProfilesStore.fetchDetail(perfilSlug.value);

  if (profile) {
    affiliateConfigData.value = mapOfficialProfileToAffiliateConfig(profile);
    bannerCategorySlug.value = profile.banner_category_slug ?? undefined;
  }

  if (affiliateConfigData.value) {
    if (
      affiliateConfigData.value.affiliateDetails?.name &&
      route.path.includes('perfil-oficial')
    ) {
      useSeo({
        title: `${affiliateConfigData.value.affiliateDetails.name} ${standardTitleSuffix}`,
      });
    }
  } else if (perfil.value && route.path.includes('perfil-oficial')) {
    router.replace('/perfil-oficial/');
  }

  isLoading.value = false;
};

onMounted(() => {
  loadAffiliateConfig();
});

const config = computed<AffiliateConfig | undefined>(() => {
  return affiliateConfigData.value;
});
</script>

<template>
  <div
    v-if="shouldRenderAffiliateContent"
    class="min-h-[80vh]"
  >
    <div
      v-if="isLoading"
      class="flex items-center justify-center min-h-[80vh]"
    >
      <div class="animate-pulse">
        <ApLogoIcon class="w-16 h-16 opacity-60" />
      </div>
    </div>
    <div v-else-if="config">
      <PerfilOficialGamesPage
        :banner-category-slug="bannerCategorySlug"
        :config="config"
        :perfil-slug="perfilSlug"
        :promocode="promocodeFromQuery"
      />
    </div>
  </div>
  <div
    v-else-if="!shouldRenderAffiliateContent || !config"
    class="flex items-center justify-center min-h-[80vh]"
  >
    <div class="animate-pulse">
      <ApLogoIcon class="w-16 h-16 opacity-60" />
    </div>
  </div>
</template>
